import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { ClipboardList } from 'lucide-react';
import {
  HYROS_FUNNEL_EVENTS,
  trackHyrosFunnelEvent,
  trackHyrosFunnelSurveyStep,
} from '@/lib/analytics/hyros-funnel';
import { afterStrictModeMount } from '@/lib/after-strict-mode-mount';
import { trackEvent } from '@/lib/analytics/analytics-service';
import {
  classifyGhlThankYouPayload,
  GHL_CALENDAR_THANK_YOU_MARKER,
  includesGhlCalendarThankYou,
} from '@/lib/posture-analysis/ghl-submit-action';
import logger from '@/lib/logging';
import {
  extractGhlSurveySnapshot,
  type GhlSurveySnapshot,
} from '@/lib/ghl-survey-answers';
import {
  applyGhlHarryLeadSource,
  GHL_HARRY_LEAD_SOURCE_QUERY_KEY,
  GHL_HARRY_LEAD_SOURCE_QUERY_KEY_ALIAS,
  GHL_HARRY_POSTURE_ANALYSIS_URL_QUERY_KEY,
} from '@/lib/sales-prospect-ghl-attach';
import {
  getCalendarPostureOption,
  mapToCalendlyPostureOption,
} from './classification-display-labels';
import { splitLegacyName } from '@/lib/clients/roster-fields';
import {
  describeAttributeMutation,
  describeGhlMessageKind,
  formatSurveyAgeFreezeDebugLine,
  isSurveyAgeFreezeDebugEnabled,
  isViteHotClient,
  listHarryOuterSurveyScripts,
  persistSurveyAgeFreezeDebugEvent,
  shortUserAgent,
  shouldApplySurveyParentHeight,
  shouldDeferSurveyParentUpdate,
  snapshotIframeDom,
  snapshotSurveyIframeLayout,
  SURVEY_AGE_FREEZE_DEBUG_HUD_LIMIT,
  SURVEY_AGE_FREEZE_DEBUG_PREFIX,
  SURVEY_AGE_PARENT_UPDATE_DEFER_MS,
  type SurveyAgeFreezeDebugEvent,
  type SurveyAgeFreezeDebugSource,
} from './survey-age-freeze-debug';

export type EnsureProspectForBookingInput = {
  email: string;
  name: string;
  ghlSurvey?: GhlSurveySnapshot;
};

interface SurveyBookingWidgetProps {
  email?: string;
  name?: string;
  postureClassification?: string;
  /**
   * Absolute Harry page URL written onto GHL `harry_posture_analysis_url`.
   * Defaults to this tab’s origin + pathname (never the session cookie).
   */
  postureAnalysisUrl?: string;
  surveyUrl?: string;
  onDownloadReport?: () => void;
  isRedownloading?: boolean;
  headline?: string;
  /**
   * Unused as the destination (ADR-0011). Harry loads the published
   * `formAction.redirectUrl` after a post-grace sticky submit.
   * Top leave is still `actionType === "1"` + http(s) URL.
   */
  bookingWidgetUrl?: string;
  /**
   * Fired once on a post-grace GHL `set-sticky-contacts` submit signal.
   * Used as the Meta Lead seam. Sticky does not choose the calendar.
   */
  onSurveySubmitted?: (contact?: { name: string; email: string }) => void;
  /**
   * Name and email typed in the survey, including a later message after
   * submit. Does not check consent or download the PDF.
   */
  onSurveyContact?: (contact: { name: string; email: string }) => void;
  /**
   * Fired once when a GHL calendar booking confirmation is detected after
   * the calendar iframe is active (Meta Schedule seam). Best-effort —
   * cross-origin GHL does not expose the Schedule meeting click.
   */
  onBookingScheduled?: () => void;
  /**
   * Ensure Client Prospect by email and return prospect id for calendar
   * `harry_posture_analysis_url` prefill. Missing email / failure must not
   * block calendar open.
   */
  onEnsureProspectForBooking?: (
    input: EnsureProspectForBookingInput,
  ) => Promise<string | null>;
  /**
   * Fill the parent (booking dialog) instead of a fixed 750px iframe.
   * Avoids a nested scrolling ancestor around the GHL iframe on mobile.
   */
  fillViewport?: boolean;
}

/** sessionStorage key — survey already completed in this browser tab session. */
export const SURVEY_BOOKING_SUBMITTED_KEY =
  'harry-self-assessment-survey-booking-submitted';

export function hasSurveyBookingSubmitted(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.sessionStorage.getItem(SURVEY_BOOKING_SUBMITTED_KEY) === '1';
  } catch {
    return false;
  }
}

export function markSurveyBookingSubmitted(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(SURVEY_BOOKING_SUBMITTED_KEY, '1');
  } catch {
    /* private mode / quota — in-memory parent state still works for this view */
  }
}

export function clearSurveyBookingSubmitted(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(SURVEY_BOOKING_SUBMITTED_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * LeadConnectorHQ (GoHighLevel) survey widget for posture walkthrough booking.
 * Prefill uses URL query params that match each field's Query Key / custom field name.
 */
export const DEFAULT_SURVEY_BASE_URL =
  'https://api.leadconnectorhq.com/widget/survey/LqlEDJaFbS6i17cfJHfy';

const SURVEY_EMBED_SCRIPT_ID = 'ghl-form-embed-script';
const SURVEY_EMBED_SCRIPT_SRC = 'https://link.msgsndr.com/js/form_embed.js';

/**
 * Survey prefill query keys for LqlEDJaFbS6i17cfJHfy.
 * Posture is written onto the dropdown query key and the named alias, matching
 * the previous survey: the option string must match exactly.
 */
export const SURVEY_QUERY_KEYS = {
  fullName: 'full_name',
  email: 'email',
  /** "What was your posture classification?" — custom field query key */
  posture: 'posture_classification',
  /** Named Query Key alias; still set so either GHL binding matches. */
  postureLegacy: 'posture-classification',
} as const;

/**
 * Calendar form query keys on posture-assessment-walkthrough-call
 * (attached form QY4D2w0YD3D3tmAiqoAb). Standard contact fields plus the
 * two required posture questions.
 */
export const CALENDAR_QUERY_KEYS = {
  /** Dropdown: "What was your Posture Classification? This should have been automatically filled out…" */
  postureDropdown: 'single_dropdown_17fmt',
  /** Text: "What was your Posture Classification?" */
  postureText: 'single_line_59ixi',
  firstName: 'first_name',
  lastName: 'last_name',
  email: 'email',
  /** Named Query Key alias — some attached calendar forms bind `contact.email`. */
  emailContact: 'contact.email',
  /** Staff sales URL so specialists can open prospect posture pics */
  prospectPostureAnalysisUrl: GHL_HARRY_POSTURE_ANALYSIS_URL_QUERY_KEY,
  /**
   * Hidden TEXT custom field on attached form QY4D2w0YD3D3tmAiqoAb
   * (`contact.harry_posture_analysis_url`, field id CA8uWIn9y2q30o0WuEf2).
   */
  postureAnalysisUrl: GHL_HARRY_POSTURE_ANALYSIS_URL_QUERY_KEY,
  leadSource: GHL_HARRY_LEAD_SOURCE_QUERY_KEY,
  leadSourceAlias: GHL_HARRY_LEAD_SOURCE_QUERY_KEY_ALIAS,
} as const;

/** Value written onto `harry_lead_source` / `source`. */
export const GHL_HARRY_LEAD_SOURCE = 'hArry - harryposture.com';

export const SURVEY_WIDGET_ID = 'LqlEDJaFbS6i17cfJHfy';

/**
 * GHL survey widget flag. When present, the widget does not load the Meta
 * pixel and does not call fbq. The submission still includes eventData.fbEventId,
 * so GoHighLevel can send one server-side SubmitApplication.
 *
 * The survey widget fires the browser event without that id. GHL forms pass
 * the same id on both sides, and Meta collapses them. This survey does not,
 * so each real submit was counted more than once. Ads showed 166 Website
 * Submit Application results against 42 GoHighLevel submissions.
 */
export const SURVEY_META_NOTRACK_QUERY_KEY = 'notrack';

/**
 * iframe height — booking calendar needs more room than the survey alone.
 * The booking dialog uses `fillViewport` so this is a fallback for inline embeds.
 *
 * NEVER add a sandbox attribute to this iframe: verified live (2026-08) that any
 * sandbox breaks the Cloudflare Turnstile challenge inside the GHL widget, which
 * silently blocks submission entirely ("Please complete the challenge and try
 * again"). This survey is Message (`actionType` `"2"`): stay in-frame after
 * submit. Harry sets `iframe.src` to the published `redirectUrl` when this
 * submit’s thank-you is the calendar ending (ADR-0011), or as a fallback when
 * sticky income/age qualifies. Guide / slots-filled copy stays put. If GHL
 * later publishes `actionType === "1"` + a URL, GHL sets `_top`.
 *
 * Do not put this iframe under a CSS-transformed ancestor (Radix centered
 * dialog `translate(-50%)` / `zoom-in`): browsers then mis-map taps inside
 * the iframe, so GHL Next is dead on mobile.
 */
export const SURVEY_IFRAME_HEIGHT_PX = 750;

/**
 * GHL booking calendar is a two-column date + slot picker taller than the
 * survey. In the viewport dialog the iframe must be this tall (or taller if
 * GHL posts a height) so CardContent can scroll to later time slots.
 */
export const CALENDAR_IFRAME_HEIGHT_PX = 1200;

/** Best-effort pixel height from a GHL embed resize postMessage. */
export function extractGhlIframeHeightPx(data: unknown): number | null {
  const asPx = (value: unknown): number | null => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return null;
    const rounded = Math.round(value);
    if (rounded < 200 || rounded > 8000) return null;
    return rounded;
  };
  if (Array.isArray(data) && (data[0] === 'setHeight' || data[0] === 'height')) {
    return asPx(data[1]);
  }
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const rec = data as Record<string, unknown>;
    return asPx(rec.height) ?? asPx(rec.scrollHeight) ?? asPx(rec.iframeHeight);
  }
  return asPx(data);
}

/**
 * LeadConnector booking calendar shown in-frame after survey submission
 * (in-frame handoff replaces GHL's top-level "Redirect to URL", which would
 * otherwise take over the whole tab).
 */
export const DEFAULT_BOOKING_WIDGET_URL =
  'https://api.leadconnectorhq.com/widget/bookings/posture-assessment-walkthrough-call';

/**
 * Thank-you copy used in GHL Settings ("Thanks! Loading the calendar…").
 * Kept for marker helpers / optional handoff pixel; the live GHL iframe is
 * cross-origin so the parent cannot read this text from the DOM.
 */
export const CALENDAR_HANDOFF_MARKER = GHL_CALENDAR_THANK_YOU_MARKER;

/** postMessage.type from the optional same-origin handoff pixel in thank-you HTML. */
export const CALENDAR_HANDOFF_MESSAGE_TYPE = 'harry-ghl-calendar-handoff';

/**
 * postMessage.type from the hidden survey HTML bridge.
 * GHL's submit sticky often has full_name and omits email; this message
 * carries the address the visitor typed in `#email`.
 */
export const HARRY_SURVEY_CONTACT_MESSAGE_TYPE = 'harry-survey-contact';

const GHL_MESSAGE_ORIGINS = ['leadconnectorhq.com', 'msgsndr.com'];

/** True when the postMessage origin belongs to the embedded GHL widget. */
export function isGhlWidgetOrigin(origin: string): boolean {
  try {
    const { hostname } = new URL(origin);
    return GHL_MESSAGE_ORIGINS.some(
      (domain) => hostname === domain || hostname.endsWith(`.${domain}`)
    );
  } catch {
    return false;
  }
}

const GHL_BOOKING_THANK_YOU_HOST = 'conorharris.com';
const GHL_BOOKING_THANK_YOU_PATH = '/posture-call-booked';

/** True when origin is the GHL calendar thank-you host (www optional). */
export function isGhlBookingThankYouOrigin(origin: string): boolean {
  try {
    const host = new URL(origin).hostname.replace(/^www\./, '').toLowerCase();
    return host === GHL_BOOKING_THANK_YOU_HOST;
  } catch {
    return false;
  }
}

/**
 * True when a URL/string is GHL's booked landing (`conorharris.com/posture-call-booked`).
 * Used for iframe src, postMessage payloads, and Navigation API `_top` redirects.
 */
export function isGhlBookingThankYouUrl(value: string): boolean {
  if (!value) return false;
  const lower = value.toLowerCase();
  if (lower.includes('posture-call-booked')) return true;
  try {
    const parsed = new URL(value);
    const host = parsed.hostname.replace(/^www\./, '');
    const path = parsed.pathname.replace(/\/+$/, '') || '/';
    return host === GHL_BOOKING_THANK_YOU_HOST && path === GHL_BOOKING_THANK_YOU_PATH;
  } catch {
    return false;
  }
}

/**
 * Handoff pixel may post from Harry (same-origin nested iframe), a `data:`
 * iframe (`origin === "null"`), or GHL. Reject everything else.
 */
export function isAllowedCalendarHandoffOrigin(origin: string): boolean {
  if (origin === 'null') return true;
  if (typeof window !== 'undefined' && origin === window.location.origin) return true;
  return isGhlWidgetOrigin(origin);
}

/**
 * Ignore qualifying sticky-contact messages for this long after mount so GHL's
 * returning-contact restore cannot skip the survey form.
 */
export const SURVEY_CALENDAR_SWAP_GRACE_MS = 2500;

/**
 * After a GHL submit, watch this long for calendar vs stay-put thank-you copy
 * (readable same-origin iframe or a later postMessage). Cross-origin GHL
 * bodies are unreadable — then only an explicit calendar handoff loads.
 */
export const CALENDAR_HANDOFF_WATCH_MS = 8000;

export const CALENDAR_HANDOFF_POLL_MS = 250;

/**
 * Income brackets that qualify for the in-iframe booking calendar after submit.
 * Stay on the GHL message when income is under $75k (`$0-$50,000`,
 * `$50,000-$75,000`, and legacy `Under $50,000`).
 * Verified live against survey C8UQSwlwEvmsX6PpPBxj (2026-08).
 */
export const CALENDAR_ELIGIBLE_INCOME_LEVELS = [
  '$75,000-$100,000',
  '$100,000-$150,000',
  '$150,000+',
] as const;

/** Sticky-contact field ids that carry the yearly income dropdown value. */
export const SURVEY_INCOME_FIELD_KEYS = [
  'XrzKujMIvdMKeYOTFC8P',
  'single_dropdown_12j9m_13l8k',
  'contact.single_dropdown_12j9m_13l8k',
  'contact.XrzKujMIvdMKeYOTFC8P',
] as const;

/** All General Income dropdown options on survey C8UQSwlwEvmsX6PpPBxj. */
export const SURVEY_INCOME_LEVELS = [
  '$0-$50,000',
  '$50,000-$75,000',
  'Under $50,000',
  ...CALENDAR_ELIGIBLE_INCOME_LEVELS,
] as const;

/**
 * Age brackets that stay on the GHL message (no calendar).
 * Live survey option is `18-25`; also accept `18-26` if the label is renamed.
 */
export const CALENDAR_DISQUALIFIED_AGE_BRACKETS = [
  '18-25',
  '18-26',
  '61+',
] as const;

/** All Age dropdown options on survey C8UQSwlwEvmsX6PpPBxj. */
export const SURVEY_AGE_BRACKETS = [
  '18-25',
  '18-26',
  '26-30',
  '31-40',
  '41-50',
  '51-60',
  '61+',
] as const;

/**
 * Sticky-contact field ids that carry the age dropdown value.
 * GHL posts the custom-field id (`Ezs5bJMzyAuBGZevNG7m`), not the query key.
 */
export const SURVEY_AGE_FIELD_KEYS = [
  'Ezs5bJMzyAuBGZevNG7m',
  'single_dropdown_164gc',
  'contact.age',
  'age',
] as const;

/**
 * Detect the GHL survey submission postMessage sent from the iframe to the parent.
 *
 * Verified live against survey C8UQSwlwEvmsX6PpPBxj (2026-08): on successful
 * submission the widget posts
 *   ['set-sticky-contacts', '_ud', '{"customer_id":"…","full_name":"…",…}', locationId, uuid]
 * Sticky JSON is a Lead signal only — not a navigation gate (ADR-0010).
 */
export function isSurveySubmissionMessage(data: unknown): boolean {
  if (!data) return false;
  if (Array.isArray(data)) {
    if (data[0] === 'set-sticky-contacts') return true;
    return data.some(
      (entry) => typeof entry === 'string' && entry.includes('customer_id')
    );
  }
  if (typeof data === 'object') {
    return (data as { type?: unknown }).type === 'FORM_SUBMITTED';
  }
  return false;
}

/** Parse the sticky-contact JSON object from a GHL submission postMessage. */
export function extractStickyContactPayload(
  data: unknown
): Record<string, unknown> | null {
  if (!Array.isArray(data)) return null;
  for (const entry of data) {
    if (typeof entry !== 'string' || !entry.includes('customer_id')) continue;
    try {
      const parsed = JSON.parse(entry) as unknown;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      /* ignore malformed sticky blob */
    }
  }
  return null;
}

function isKnownIncomeLevel(value: string): boolean {
  const normalized = value.replace(/\s/g, '');
  return (SURVEY_INCOME_LEVELS as readonly string[]).some(
    (level) => level.replace(/\s/g, '') === normalized,
  );
}

/** Read the yearly income value from a sticky-contact / submission payload. */
export function extractSurveyIncomeLevel(data: unknown): string | null {
  const sticky = extractStickyContactPayload(data);
  if (!sticky) return null;
  for (const key of SURVEY_INCOME_FIELD_KEYS) {
    const value = sticky[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  for (const value of Object.values(sticky)) {
    if (typeof value !== 'string') continue;
    const trimmed = value.trim();
    if (isKnownIncomeLevel(trimmed)) return trimmed;
  }
  return null;
}

export function isCalendarEligibleIncome(income: string | null | undefined): boolean {
  if (!income) return false;
  const normalized = income.replace(/\s/g, '');
  return (CALENDAR_ELIGIBLE_INCOME_LEVELS as readonly string[]).some(
    (level) => level.replace(/\s/g, '') === normalized,
  );
}

/** Read the age bracket from a sticky-contact / submission payload. */
export function extractSurveyAge(data: unknown): string | null {
  const sticky = extractStickyContactPayload(data);
  if (!sticky) return null;
  for (const key of SURVEY_AGE_FIELD_KEYS) {
    const value = sticky[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  for (const value of Object.values(sticky)) {
    if (typeof value !== 'string') continue;
    const trimmed = value.trim();
    if ((SURVEY_AGE_BRACKETS as readonly string[]).includes(trimmed)) return trimmed;
  }
  return null;
}

/** Sticky-contact field ids that carry the survey full name. */
export const SURVEY_NAME_FIELD_KEYS = [
  'full_name',
  'contact.full_name',
  'name',
] as const;

/** Sticky-contact field ids that carry a dedicated last-name value. */
export const SURVEY_LAST_NAME_FIELD_KEYS = [
  'last_name',
  'contact.last_name',
] as const;

/** Sticky-contact field ids that carry the survey email. */
export const SURVEY_EMAIL_FIELD_KEYS = [
  'email',
  'contact.email',
  'Email',
  'email_address',
] as const;

const SURVEY_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;


/** Read the typed survey name from a sticky-contact / submission payload. */
export function extractSurveyFullName(data: unknown): string | null {
  const sticky = extractStickyContactPayload(data);
  if (!sticky) return null;
  for (const key of SURVEY_NAME_FIELD_KEYS) {
    const value = sticky[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  const first = typeof sticky.first_name === 'string' ? sticky.first_name.trim() : '';
  const last = typeof sticky.last_name === 'string' ? sticky.last_name.trim() : '';
  const combined = [first, last].filter(Boolean).join(' ');
  return combined || null;
}

/** Read a dedicated last-name field from sticky (not a split of full_name). */
export function extractSurveyLastName(data: unknown): string | null {
  const sticky = extractStickyContactPayload(data);
  if (!sticky) return null;
  for (const key of SURVEY_LAST_NAME_FIELD_KEYS) {
    const value = sticky[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return null;
}

function findEmailString(value: unknown, depth = 0): string | null {
  if (depth > 6 || value == null) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim().toLowerCase();
    return SURVEY_EMAIL_RE.test(trimmed) ? trimmed : null;
  }
  if (Array.isArray(value)) {
    for (const entry of value) {
      const found = findEmailString(entry, depth + 1);
      if (found) return found;
    }
    return null;
  }
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    for (const key of SURVEY_EMAIL_FIELD_KEYS) {
      const found = findEmailString(record[key], depth + 1);
      if (found) return found;
    }
    for (const [key, nested] of Object.entries(record)) {
      if (/mail/i.test(key)) {
        const found = findEmailString(nested, depth + 1);
        if (found) return found;
      }
    }
    for (const nested of Object.values(record)) {
      const found = findEmailString(nested, depth + 1);
      if (found) return found;
    }
  }
  return null;
}

/** Name and email from the in-survey contact bridge. Invalid emails are dropped. */
export function extractHarrySurveyContact(
  data: unknown,
): { name: string; email: string } | null {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  if ((data as { type?: unknown }).type !== HARRY_SURVEY_CONTACT_MESSAGE_TYPE) return null;
  const nameValue = (data as { name?: unknown }).name;
  const emailValue = (data as { email?: unknown }).email;
  const name = typeof nameValue === 'string' ? nameValue.trim() : '';
  const emailRaw = typeof emailValue === 'string' ? emailValue.trim().toLowerCase() : '';
  const email = SURVEY_EMAIL_RE.test(emailRaw) ? emailRaw : '';
  if (!name && !email) return null;
  return { name, email };
}

/** Read the survey email from a sticky-contact / submission payload. */
export function extractSurveyEmail(data: unknown): string | null {
  const sticky = extractStickyContactPayload(data);
  if (!sticky) return findEmailString(data);
  return findEmailString(sticky);
}

/** Email carried on the optional same-origin thank-you handoff pixel. */
export function extractHandoffPixelEmail(data: unknown): string | null {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  if ((data as { type?: unknown }).type !== CALENDAR_HANDOFF_MESSAGE_TYPE) return null;
  return findEmailString((data as { email?: unknown }).email);
}

export function isCalendarDisqualifiedAge(age: string | null | undefined): boolean {
  if (!age) return false;
  const normalized = age.replace(/\s/g, '');
  return (CALENDAR_DISQUALIFIED_AGE_BRACKETS as readonly string[]).some(
    (bracket) => bracket.replace(/\s/g, '') === normalized,
  );
}

/**
 * True when sticky-contacts income is $75k+ and age is not 18–26 or 61+.
 * GHL’s cross-origin iframe means Harry cannot read the thank-you text; sticky
 * field values are the gate distinguishing qualify vs stay-on-message.
 */
export function shouldSwapToBookingCalendar(data: unknown): boolean {
  const age = extractSurveyAge(data);
  if (isCalendarDisqualifiedAge(age)) return false;
  const income = extractSurveyIncomeLevel(data);
  return isCalendarEligibleIncome(income);
}

/** Non-secret cache-bust so sticky restore is less likely to clobber this run's posture. */
export const SURVEY_CACHE_BUST_QUERY_KEY = 'harry_cb';

export function createSurveyCacheBust(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().replace(/-/g, '').slice(0, 12);
  }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

/** Normalize ellipsis variants so "…" and "..." both match the marker. */
export function includesCalendarHandoffMarker(text: string): boolean {
  return includesGhlCalendarThankYou(text);
}

/**
 * True when a postMessage payload indicates the calendar thank-you path:
 * - explicit `{ type: 'harry-ghl-calendar-handoff' }` from the handoff pixel
 * - payload copy classified as the calendar ending (not guide / slots-filled)
 */
export function isCalendarHandoffMessage(data: unknown): boolean {
  if (!data) return false;
  if (typeof data === 'object' && !Array.isArray(data)) {
    if ((data as { type?: unknown }).type === CALENDAR_HANDOFF_MESSAGE_TYPE) {
      return true;
    }
  }
  return classifyGhlThankYouPayload(data) === 'calendar';
}

/**
 * Markers that GHL booking widgets may post after Schedule meeting succeeds.
 * Prefer under-count: do not treat survey guide / calendar-handoff copy as booked.
 */
const BOOKING_CONFIRMATION_MARKERS = [
  'appointment confirmed',
  'appointment scheduled',
  "you're booked",
  'you are booked',
  'you\'re booked',
  'booking confirmed',
  'meeting scheduled',
  'successfully scheduled',
  'your meeting has been scheduled',
  'your appointment has been scheduled',
  'thank you for booking',
] as const;

function flattenMessageText(data: unknown): string {
  if (typeof data === 'string') return data;
  try {
    return JSON.stringify(data) ?? '';
  } catch {
    return '';
  }
}

/**
 * True when a postMessage payload looks like a completed GHL calendar booking.
 * Used only after the calendar iframe is active (Meta Schedule seam).
 */
export function isGhlBookingConfirmationMessage(data: unknown): boolean {
  if (!data) return false;
  if (isCalendarHandoffMessage(data)) return false;
  if (classifyGhlThankYouPayload(data) === 'stay') return false;
  const text = flattenMessageText(data).toLowerCase();
  if (!text) return false;
  if (isGhlBookingThankYouUrl(text)) return true;
  return BOOKING_CONFIRMATION_MARKERS.some((marker) => text.includes(marker));
}

function previewGhlMessageData(data: unknown): string {
  try {
    const raw = typeof data === 'string' ? data : JSON.stringify(data);
    return raw.length > 500 ? `${raw.slice(0, 500)}…` : raw;
  } catch {
    return '[unserializable]';
  }
}

function applyPosturePrefill(
  setParam: (key: string, value: string) => void,
  postureOption: string,
): void {
  setParam(SURVEY_QUERY_KEYS.posture, postureOption);
  setParam(SURVEY_QUERY_KEYS.postureLegacy, postureOption);
}

/** Best-effort iframe location candidates (src attr; contentWindow when same-origin). */
export function readIframeLocationCandidates(iframe: HTMLIFrameElement): string[] {
  const candidates = [iframe.src, iframe.getAttribute('src') ?? ''];
  try {
    const href = iframe.contentWindow?.location?.href;
    if (href) candidates.push(href);
  } catch {
    /* cross-origin — src attr is the only signal */
  }
  return candidates.filter(Boolean);
}

/** Best-effort read of iframe body text (null when cross-origin / inaccessible). */
export function readIframeBodyText(iframe: HTMLIFrameElement): string | null {
  try {
    const body = iframe.contentDocument?.body;
    if (!body) return null;
    return body.innerText || body.textContent || null;
  } catch {
    return null;
  }
}

/**
 * Build a LeadConnectorHQ survey URL with query-param prefill.
 * Spaces are encoded as %20 so dropdown option strings match exactly.
 */
export function buildSurveyBookingUrl(options: {
  baseUrl?: string;
  name?: string;
  email?: string;
  postureClassification?: string;
  utmSource?: string;
  cacheBust?: string;
}): string {
  const {
    baseUrl = DEFAULT_SURVEY_BASE_URL,
    name = '',
    postureClassification = '',
    utmSource,
    cacheBust = createSurveyCacheBust(),
    email = '',
  } = options;
  const targetPosture = mapToCalendlyPostureOption(postureClassification);
  const fullName = name.trim();
  const emailValue = email.trim();
  const bust = cacheBust.trim();

  try {
    const urlObj = new URL(baseUrl);
    if (utmSource) urlObj.searchParams.set('utm_source', utmSource);
    if (fullName) urlObj.searchParams.set(SURVEY_QUERY_KEYS.fullName, fullName);
    if (emailValue) urlObj.searchParams.set(SURVEY_QUERY_KEYS.email, emailValue);
    if (targetPosture) {
      applyPosturePrefill((key, value) => urlObj.searchParams.set(key, value), targetPosture);
    }
    if (bust) urlObj.searchParams.set(SURVEY_CACHE_BUST_QUERY_KEY, bust);
    urlObj.searchParams.set(SURVEY_META_NOTRACK_QUERY_KEY, 'true');
    urlObj.searchParams.delete('harry_sa_session');
    // Prefer %20 over + for option-string matching consistency
    return urlObj.toString().replace(/\+/g, '%20');
  } catch {
    const params: string[] = [];
    if (utmSource) params.push(`utm_source=${encodeURIComponent(utmSource)}`);
    if (fullName) {
      params.push(`${SURVEY_QUERY_KEYS.fullName}=${encodeURIComponent(fullName)}`);
    }
    if (emailValue) {
      params.push(`${SURVEY_QUERY_KEYS.email}=${encodeURIComponent(emailValue)}`);
    }
    if (targetPosture) {
      applyPosturePrefill((key, value) => {
        params.push(`${key}=${encodeURIComponent(value)}`);
      }, targetPosture);
    }
    if (bust) {
      params.push(`${SURVEY_CACHE_BUST_QUERY_KEY}=${encodeURIComponent(bust)}`);
    }
    params.push(`${SURVEY_META_NOTRACK_QUERY_KEY}=true`);
    const queryString = params.join('&');
    if (!queryString) return baseUrl;
    return baseUrl.includes('?') ? `${baseUrl}&${queryString}` : `${baseUrl}?${queryString}`;
  }
}

function applyCalendarPosturePrefill(
  setParam: (key: string, value: string) => void,
  postureOption: string,
): void {
  setParam(CALENDAR_QUERY_KEYS.postureDropdown, postureOption);
  setParam(CALENDAR_QUERY_KEYS.postureText, postureOption);
}

/**
 * Absolute Harry page URL for GHL `harry_posture_analysis_url`.
 * Origin + pathname only — never search/hash, never `harry_sa_session`.
 */
export function resolveHarryPostureAnalysisUrl(explicit?: string): string {
  const raw =
    explicit?.trim() ||
    (typeof window !== 'undefined'
      ? `${window.location.origin}${window.location.pathname}`
      : '');
  if (!raw) return '';
  try {
    const parsed = new URL(
      raw,
      typeof window !== 'undefined' ? window.location.origin : 'https://invalid.local',
    );
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return '';
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    return '';
  }
}

/**
 * Build the LeadConnectorHQ booking-calendar URL with query-param prefill
 * for both required posture fields, first/last name, and email taken from
 * the survey sticky payload (Harry lead fields are the fallback). Option
 * strings must match the calendar dropdown exactly (they differ from the
 * survey).
 */
export function buildCalendarBookingUrl(options: {
  baseUrl?: string;
  postureClassification?: string;
  name?: string;
  lastName?: string;
  email?: string;
  /** Absolute staff `/sales/prospects/:id` URL for GHL hidden field. */
  prospectPostureAnalysisUrl?: string;
  postureAnalysisUrl?: string;
}): string {
  const {
    baseUrl = DEFAULT_BOOKING_WIDGET_URL,
    postureClassification = '',
    name = '',
    lastName: lastNameOverride = '',
    email = '',
    prospectPostureAnalysisUrl = '',
    postureAnalysisUrl,

  } = options;
  const targetPosture = postureClassification
    ? getCalendarPostureOption(postureClassification)
    : '';
  const { firstName, lastName: splitLastName } = splitLegacyName(name);
  const lastName = lastNameOverride.trim() || splitLastName;
  const emailValue = email.trim();
  const staffUrl = prospectPostureAnalysisUrl.trim();
  const analysisUrl = staffUrl || resolveHarryPostureAnalysisUrl(postureAnalysisUrl);

  try {
    const urlObj = new URL(baseUrl);
    if (targetPosture) {
      applyCalendarPosturePrefill(
        (key, value) => urlObj.searchParams.set(key, value),
        targetPosture,
      );
    }
    if (firstName) urlObj.searchParams.set(CALENDAR_QUERY_KEYS.firstName, firstName);
    if (lastName) urlObj.searchParams.set(CALENDAR_QUERY_KEYS.lastName, lastName);
    if (emailValue) {
      urlObj.searchParams.set(CALENDAR_QUERY_KEYS.email, emailValue);
      urlObj.searchParams.set(CALENDAR_QUERY_KEYS.emailContact, emailValue);
    }
    if (analysisUrl) {
      urlObj.searchParams.set(CALENDAR_QUERY_KEYS.prospectPostureAnalysisUrl, analysisUrl);
    }
    applyGhlHarryLeadSource((key, value) => urlObj.searchParams.set(key, value));
    urlObj.searchParams.delete('harry_sa_session');
    return urlObj.toString().replace(/\+/g, '%20');
  } catch {
    const params: string[] = [];
    if (targetPosture) {
      applyCalendarPosturePrefill((key, value) => {
        params.push(`${key}=${encodeURIComponent(value)}`);
      }, targetPosture);
    }
    if (firstName) {
      params.push(`${CALENDAR_QUERY_KEYS.firstName}=${encodeURIComponent(firstName)}`);
    }
    if (lastName) {
      params.push(`${CALENDAR_QUERY_KEYS.lastName}=${encodeURIComponent(lastName)}`);
    }
    if (emailValue) {
      params.push(`${CALENDAR_QUERY_KEYS.email}=${encodeURIComponent(emailValue)}`);
      params.push(`${CALENDAR_QUERY_KEYS.emailContact}=${encodeURIComponent(emailValue)}`);
    }
    if (analysisUrl) {
      params.push(
        `${CALENDAR_QUERY_KEYS.prospectPostureAnalysisUrl}=${encodeURIComponent(analysisUrl)}`,
      );
    }
    applyGhlHarryLeadSource((key, value) => {
      params.push(`${key}=${encodeURIComponent(value)}`);
    });
    const queryString = params.join('&');
    if (!queryString) return baseUrl;
    return baseUrl.includes('?') ? `${baseUrl}&${queryString}` : `${baseUrl}?${queryString}`;
  }
}

/** How long calendar handoff may wait for prospect-id before loading without the staff URL. */
export const PROSPECT_ENSURE_BUDGET_MS = 2500;

/**
 * After a calendar thank-you pixel with no email yet, wait this long for a
 * follow-up sticky before opening the calendar without it.
 */
export const EMAIL_HANDOFF_WAIT_MS = 500;

export const SurveyBookingWidget: React.FC<SurveyBookingWidgetProps> = ({
  email = '',
  name = '',
  postureClassification = '',
  surveyUrl = DEFAULT_SURVEY_BASE_URL,
  headline,
  bookingWidgetUrl: _bookingWidgetUrl,
  onSurveySubmitted,
  onSurveyContact,
  onBookingScheduled,
  onEnsureProspectForBooking,
  onDownloadReport,
  fillViewport = false,
}) => {
  const targetPosture = mapToCalendlyPostureOption(postureClassification);
  const onSurveySubmittedRef = React.useRef(onSurveySubmitted);
  onSurveySubmittedRef.current = onSurveySubmitted;
  const onSurveyContactRef = React.useRef(onSurveyContact);
  onSurveyContactRef.current = onSurveyContact;
  const onBookingScheduledRef = React.useRef(onBookingScheduled);
  onBookingScheduledRef.current = onBookingScheduled;
  const onEnsureProspectRef = React.useRef(onEnsureProspectForBooking);
  onEnsureProspectRef.current = onEnsureProspectForBooking;
  const onDownloadReportRef = React.useRef(onDownloadReport);
  onDownloadReportRef.current = onDownloadReport;
  const emailPropRef = React.useRef(email);
  emailPropRef.current = email;
  const namePropRef = React.useRef(name);
  namePropRef.current = name;
  const postureRef = React.useRef(postureClassification);
  postureRef.current = postureClassification;
  const submittedRef = React.useRef(false);
  const surveyOpenedTrackedRef = React.useRef(false);
  const pendingCalendarHandoffRef = React.useRef(false);
  const calendarHandoffStartedRef = React.useRef(false);
  const pdfRedirectedRef = React.useRef(false);
  const bookingScheduledRef = React.useRef(false);
  /** Sticky fallback only — thank-you/handoff is the ADR-0011 gate. */
  const calendarEligibleRef = React.useRef(false);
  const surveyEmailRef = React.useRef('');
  const surveyFullNameRef = React.useRef('');
  const surveyLastNameRef = React.useRef('');
  const surveySnapshotRef = React.useRef<GhlSurveySnapshot | null>(null);
  const mountedAtRef = React.useRef(Date.now());
  const emailWaitTimerRef = React.useRef(0);
  const loadCalendarRef = React.useRef(false);
  const [reportReady, setReportReady] = useState(false);
  const [ghlIframeHeightPx, setGhlIframeHeightPx] = useState<number | null>(null);
  const [ageFreezeDebugEvents, setAgeFreezeDebugEvents] = useState<SurveyAgeFreezeDebugEvent[]>([]);
  const cacheBustRef = React.useRef<string | null>(null);
  if (cacheBustRef.current === null) {
    cacheBustRef.current = createSurveyCacheBust();
  }
  const ghlIframeHeightPxRef = React.useRef(ghlIframeHeightPx);
  ghlIframeHeightPxRef.current = ghlIframeHeightPx;
  const iframeSrcRef = React.useRef('');
  const messageTimesRef = React.useRef<number[]>([]);
  const showAgeFreezeHud = isSurveyAgeFreezeDebugEnabled();
  const lastAgeRef = React.useRef<string | null>(null);
  const fillViewportRef = React.useRef(fillViewport);
  fillViewportRef.current = fillViewport;
  const prefilledUrl = React.useMemo(
    () =>
      buildSurveyBookingUrl({
        baseUrl: surveyUrl,
        name,
        email,
        postureClassification,
        utmSource: 'harry-self-assessment',
        cacheBust: cacheBustRef.current ?? undefined,
      }),
    [surveyUrl, name, email, postureClassification],
  );

  // Survey submit opens the PDF. The walkthrough-call calendar is not loaded.
  const iframeSrc = prefilledUrl;
  const iframeId = `inline-${SURVEY_WIDGET_ID}`;

  const confirmSurveySubmitted = React.useCallback(() => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    markSurveyBookingSubmitted();
    void trackEvent('survey_submitted', { source: 'SurveyBookingWidget' });
    logger.info('[SurveyBookingWidget] survey_submitted tracked');
    onSurveySubmittedRef.current?.({
      name: surveyFullNameRef.current.trim() || namePropRef.current.trim(),
      email: surveyEmailRef.current.trim() || emailPropRef.current.trim().toLowerCase(),
    });
  }, []);

  const confirmBookingScheduled = React.useCallback(() => {
    if (!loadCalendarRef.current) return;
    if (bookingScheduledRef.current) return;
    bookingScheduledRef.current = true;
    confirmSurveySubmitted();
    void trackEvent('walkthrough_call_booked', { source: 'SurveyBookingWidget' });
    logger.info(
      '[SurveyBookingWidget] GHL booking confirmation detected; walkthrough_call_booked tracked',
    );
    onBookingScheduledRef.current?.();
  }, [confirmSurveySubmitted]);

  const maybeConfirmBookingFromIframe = React.useCallback(() => {
    if (!loadCalendarRef.current) return;
    const iframe = document.getElementById(iframeId) as HTMLIFrameElement | null;
    if (!iframe) return;
    if (readIframeLocationCandidates(iframe).some(isGhlBookingThankYouUrl)) {
      confirmBookingScheduled();
    }
  }, [confirmBookingScheduled, iframeId]);

  const persistSurveyLead = React.useCallback(() => {
    const resolvedName =
      surveyFullNameRef.current.trim() || namePropRef.current.trim() || '';
    const resolvedEmail =
      surveyEmailRef.current.trim() || emailPropRef.current.trim().toLowerCase() || '';
    if (!resolvedEmail || !onEnsureProspectRef.current) return;
    void onEnsureProspectRef.current({
      email: resolvedEmail,
      name: resolvedName || resolvedEmail,
      ...(surveySnapshotRef.current ? { ghlSurvey: surveySnapshotRef.current } : {}),
    }).catch((err) => {
      logger.error('[SurveyBookingWidget] persist survey lead failed', {
        error: err instanceof Error ? err.message : String(err),
      });
    });
  }, []);

  const publishSurveyContact = React.useCallback(() => {
    const name = surveyFullNameRef.current.trim();
    const email = surveyEmailRef.current.trim();
    if (!name && !email) return;
    onSurveyContactRef.current?.({ name, email });
  }, []);

  const rememberSurveyFromSticky = React.useCallback((data: unknown) => {
    const snapshot = extractGhlSurveySnapshot(data);
    if (snapshot) {
      surveySnapshotRef.current = {
        ...snapshot,
        submittedAt: new Date().toISOString(),
      };
    }
    const stickyName = extractSurveyFullName(data);
    if (stickyName) {
      surveyFullNameRef.current = stickyName;
    }
    const stickyEmail = extractSurveyEmail(data);
    if (stickyEmail) {
      surveyEmailRef.current = stickyEmail;
    }
    const stickyLastName = extractSurveyLastName(data);
    if (stickyLastName) {
      surveyLastNameRef.current = stickyLastName;
    }
  }, []);

  const finishSurveyWithPdf = React.useCallback(() => {
    if (pdfRedirectedRef.current) return;
    pdfRedirectedRef.current = true;
    calendarHandoffStartedRef.current = true;
    pendingCalendarHandoffRef.current = false;
    if (emailWaitTimerRef.current) {
      window.clearTimeout(emailWaitTimerRef.current);
      emailWaitTimerRef.current = 0;
    }
    confirmSurveySubmitted();
    persistSurveyLead();
    setReportReady(true);
    logger.info('[SurveyBookingWidget] survey submitted; opening posture report download');
    onDownloadReportRef.current?.();
  }, [confirmSurveySubmitted, persistSurveyLead]);

  const scheduleCalendarHandoff = React.useCallback(
    (_source: 'thank-you' | 'sticky') => {
      finishSurveyWithPdf();
    },
    [finishSurveyWithPdf],
  );

  const recordAgeFreezeDebug = React.useCallback(
    (partial: {
      source: SurveyAgeFreezeDebugSource;
      kind: string;
      age?: string | null;
      income?: string | null;
      postedHeight?: number | null;
      willChangeSrc?: boolean;
      action: string;
      mutation?: string | null;
      persistOnly?: boolean;
    }) => {
      const now = Date.now();
      messageTimesRef.current = [...messageTimesRef.current.filter((stamp) => now - stamp < 1000), now];
      const iframe = document.getElementById(iframeId) as HTMLIFrameElement | null;
      const layout = snapshotSurveyIframeLayout(iframe);
      const dom = snapshotIframeDom(iframe);
      const currentHeight = ghlIframeHeightPxRef.current;
      const postedHeight = partial.postedHeight ?? null;
      const age = partial.age ?? lastAgeRef.current;
      if (partial.age) {
        lastAgeRef.current = partial.age;
      }
      const event: SurveyAgeFreezeDebugEvent = {
        at: new Date(now).toISOString(),
        source: partial.source,
        kind: partial.kind,
        age,
        income: partial.income ?? null,
        postedHeight,
        currentHeight,
        willChangeHeight:
          postedHeight != null &&
          postedHeight !== currentHeight &&
          shouldApplySurveyParentHeight({
            fillViewport: fillViewportRef.current,
            postedHeight,
          }),
        willChangeSrc: Boolean(partial.willChangeSrc),
        action: partial.action,
        messagesInLastSecond: messageTimesRef.current.length,
        iframePosition: layout.position,
        iframeTop: layout.top,
        iframeHeight: layout.height,
        visualViewportHeight: layout.visualViewportHeight,
        innerHeight: layout.innerHeight,
        pointerEvents: layout.pointerEvents,
        userAgentShort: shortUserAgent(),
        inlineStyle: dom.inlineStyle,
        className: dom.className,
        srcHost: dom.srcHost,
        outerScripts: listHarryOuterSurveyScripts(),
        mutation: partial.mutation ?? null,
        ageSeen: Boolean(lastAgeRef.current),
        viteHot: isViteHotClient(),
      };
      persistSurveyAgeFreezeDebugEvent(event);
      logger.info(`${SURVEY_AGE_FREEZE_DEBUG_PREFIX} ${event.source}:${event.kind}`, event);
      if (!partial.persistOnly && isSurveyAgeFreezeDebugEnabled()) {
        setAgeFreezeDebugEvents((prev) =>
          [...prev, event].slice(-SURVEY_AGE_FREEZE_DEBUG_HUD_LIMIT),
        );
      }
    },
    [iframeId],
  );

  useEffect(() => {
    const existing = document.getElementById(SURVEY_EMBED_SCRIPT_ID);
    if (existing) {
      recordAgeFreezeDebug({
        source: 'outer-script',
        kind: 'form_embed-already-present',
        action: SURVEY_EMBED_SCRIPT_SRC,
        mutation: listHarryOuterSurveyScripts().join(',') || SURVEY_EMBED_SCRIPT_ID,
        persistOnly: true,
      });
      return;
    }
    const script = document.createElement('script');
    script.id = SURVEY_EMBED_SCRIPT_ID;
    script.src = SURVEY_EMBED_SCRIPT_SRC;
    script.async = true;
    script.addEventListener('load', () => {
      recordAgeFreezeDebug({
        source: 'outer-script',
        kind: 'form_embed-loaded',
        action: SURVEY_EMBED_SCRIPT_SRC,
        mutation: listHarryOuterSurveyScripts().join(','),
        persistOnly: true,
      });
    });
    script.addEventListener('error', () => {
      recordAgeFreezeDebug({
        source: 'outer-script',
        kind: 'form_embed-error',
        action: SURVEY_EMBED_SCRIPT_SRC,
        persistOnly: true,
      });
    });
    document.body.appendChild(script);
    recordAgeFreezeDebug({
      source: 'outer-script',
      kind: 'form_embed-injected',
      action: SURVEY_EMBED_SCRIPT_SRC,
      persistOnly: true,
    });
  }, [recordAgeFreezeDebug]);

  useEffect(() => {
    logger.info('[SurveyBookingWidget] loading GHL survey', {
      surveyId: SURVEY_WIDGET_ID,
      surveyUrl: prefilledUrl,
      postureQueryKey: SURVEY_QUERY_KEYS.posture,
      postureLegacyKey: SURVEY_QUERY_KEYS.postureLegacy,
      mappedPosture: targetPosture || null,
    });
  }, [prefilledUrl, targetPosture]);

  // Q2 funnel stage: track survey widget mount as "survey opened".
  useEffect(
    () =>
      afterStrictModeMount(() => {
        if (surveyOpenedTrackedRef.current) return;
        surveyOpenedTrackedRef.current = true;
        trackHyrosFunnelEvent(HYROS_FUNNEL_EVENTS.surveyOpened);
        void trackEvent('survey_opened', { source: 'SurveyBookingWidget' });
        logger.info('[SurveyBookingWidget] survey_opened tracked');
      }),
    [],
  );

  // Option B: listen for per-step postMessage from GHL survey slides.
  // Each slide should post: { type: 'harry-survey-step', step: 'income' }
  useEffect(() => {
    const handleSurveyStepMessage = (event: MessageEvent) => {
      if (!isGhlWidgetOrigin(event.origin) && event.origin !== window.location.origin) return;
      const data = event.data;
      if (!data || typeof data !== 'object' || Array.isArray(data)) return;
      const type = (data as Record<string, unknown>).type;
      if (type !== 'harry-survey-step') return;
      const step = String((data as Record<string, unknown>).step || '').trim();
      if (!step) return;
      logger.info('[SurveyBookingWidget] survey step tracked', { step });
      trackHyrosFunnelSurveyStep(step);
      void trackEvent('survey_step_viewed', { step, source: 'SurveyBookingWidget' });
    };
    window.addEventListener('message', handleSurveyStepMessage);
    return () => window.removeEventListener('message', handleSurveyStepMessage);
  }, []);

  useEffect(() => {
    if (!iframeSrcRef.current) {
      iframeSrcRef.current = iframeSrc;
      recordAgeFreezeDebug({
        source: 'mount',
        kind: 'survey-iframe',
        action: loadCalendarRef.current ? 'mount-calendar' : 'mount-survey',
        persistOnly: true,
      });
      return;
    }
    if (iframeSrcRef.current === iframeSrc) return;
    const previous = iframeSrcRef.current;
    iframeSrcRef.current = iframeSrc;
    const label = (url: string) => (url.includes('/widget/bookings/') ? 'calendar' : 'survey');
    recordAgeFreezeDebug({
      source: 'iframe-src',
      kind: 'src-change',
      willChangeSrc: true,
      action: `${label(previous)}->${label(iframeSrc)}`,
      persistOnly: true,
    });
  }, [iframeSrc, recordAgeFreezeDebug]);

  useEffect(() => {
    const iframe = document.getElementById(iframeId) as HTMLIFrameElement | null;
    if (!iframe || typeof MutationObserver === 'undefined') return;
    const observer = new MutationObserver((records) => {
      for (const record of records) {
        if (record.type !== 'attributes') continue;
        const mutation = describeAttributeMutation(
          record.attributeName,
          record.oldValue,
          iframe,
        );
        const afterAge = Boolean(lastAgeRef.current);
        recordAgeFreezeDebug({
          source: 'outer-script',
          kind: afterAge ? 'iframe-mutated-after-age' : 'iframe-mutated',
          action: record.attributeName || 'attribute',
          mutation,
          age: lastAgeRef.current,
          persistOnly: true,
        });
        if (record.attributeName === 'src') {
          maybeConfirmBookingFromIframe();
        }
      }
    });
    observer.observe(iframe, {
      attributes: true,
      attributeFilter: ['style', 'class', 'src', 'height', 'width', 'scrolling'],
      attributeOldValue: true,
    });
    return () => observer.disconnect();
  }, [iframeId, recordAgeFreezeDebug, maybeConfirmBookingFromIframe]);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;
    const onViewportChange = () => {
      recordAgeFreezeDebug({
        source: 'viewport',
        kind: 'visualViewport-resize',
        action: `vv=${Math.round(viewport.height)} inner=${window.innerHeight}`,
        persistOnly: true,
      });
    };
    viewport.addEventListener('resize', onViewportChange);
    viewport.addEventListener('scroll', onViewportChange);
    return () => {
      viewport.removeEventListener('resize', onViewportChange);
      viewport.removeEventListener('scroll', onViewportChange);
    };
  }, [recordAgeFreezeDebug]);

  // When = calendar thank-you / handoff pixel (ADR-0011). Sticky income/age is
  // only a fallback when GHL does not post the thank-you copy (cross-origin).
  // Guide / slots-filled copy stays on the GHL message. Known low income or
  // disqualified age must not veto a calendar thank-you — GHL already forked.
  useEffect(() => {
    const readIframeText = (): string | null => {
      const iframe = document.getElementById(iframeId) as HTMLIFrameElement | null;
      if (!iframe) return null;
      return readIframeBodyText(iframe);
    };

    const handleMessage = (event: MessageEvent) => {
      const fromGhlOrigin = isGhlWidgetOrigin(event.origin);
      const liveContact = fromGhlOrigin ? extractHarrySurveyContact(event.data) : null;
      if (liveContact) {
        if (liveContact.name) surveyFullNameRef.current = liveContact.name;
        if (liveContact.email) surveyEmailRef.current = liveContact.email;
        publishSurveyContact();
        return;
      }

      const pastGrace = Date.now() - mountedAtRef.current >= SURVEY_CALENDAR_SWAP_GRACE_MS;
      const thankYouKind = classifyGhlThankYouPayload(event.data);
      const allowedHandoff = isAllowedCalendarHandoffOrigin(event.origin);
      const fromGhl = fromGhlOrigin;
      const fromThankYouHost = isGhlBookingThankYouOrigin(event.origin);
      const thankYouUrl = isGhlBookingThankYouUrl(flattenMessageText(event.data));
      const isSticky = fromGhl && isSurveySubmissionMessage(event.data);
      const isCalendar = isCalendarHandoffMessage(event.data) && allowedHandoff;
      const isStay = thankYouKind === 'stay' && (allowedHandoff || fromGhl);
      const calendarActive = loadCalendarRef.current;
      const isBookingConfirm =
        calendarActive &&
        (fromGhl || fromThankYouHost || thankYouUrl) &&
        (isGhlBookingConfirmationMessage(event.data) || thankYouUrl || fromThankYouHost);

      const stickyEligible = isSticky ? shouldSwapToBookingCalendar(event.data) : false;
      const postedHeight = fromGhl ? extractGhlIframeHeightPx(event.data) : null;
      const age = extractSurveyAge(event.data);
      const deferParentUpdate = shouldDeferSurveyParentUpdate({
        age,
        postedHeight,
        lastAge: lastAgeRef.current,
      });
      const applyParentHeight = shouldApplySurveyParentHeight({
        fillViewport: fillViewportRef.current,
        postedHeight,
      });
      if (postedHeight != null && applyParentHeight && !deferParentUpdate) {
        setGhlIframeHeightPx(postedHeight);
      } else if (postedHeight != null && applyParentHeight) {
        logger.info(`${SURVEY_AGE_FREEZE_DEBUG_PREFIX} parent-update-deferred`, {
          reason: 'age-select',
          postedHeight,
          age,
          viteHot: isViteHotClient(),
          deferMs: SURVEY_AGE_PARENT_UPDATE_DEFER_MS,
        });
        window.setTimeout(() => setGhlIframeHeightPx(postedHeight), SURVEY_AGE_PARENT_UPDATE_DEFER_MS);
      } else if (postedHeight != null) {
        logger.info(`${SURVEY_AGE_FREEZE_DEBUG_PREFIX} parent-update-skipped`, {
          reason: 'fill-viewport',
          postedHeight,
          age,
          viteHot: isViteHotClient(),
        });
      }

      // Post-calendar sticky is not a booking. GHL re-posts sticky when the
      // calendar loads; counting it as booked inflated Meta and skipped webhook.
      if (calendarActive && fromGhl && isSticky && !isBookingConfirm) {
        logger.info('[SurveyBookingWidget] GHL postMessage', {
          origin: event.origin,
          kind: 'post-calendar-sticky',
          pastGrace,
          submitted: submittedRef.current,
          calendarActive: true,
          payloadPreview: previewGhlMessageData(event.data),
          action: 'ignore-booked',
        });
        return;
      }

      if (isBookingConfirm) {
        logger.info('[SurveyBookingWidget] GHL postMessage', {
          origin: event.origin,
          kind: 'booking-confirm',
          pastGrace,
          submitted: submittedRef.current,
          calendarActive: true,
          payloadPreview: previewGhlMessageData(event.data),
          action: 'schedule',
        });
        confirmBookingScheduled();
        return;
      }

      let action = 'ignored';
      if (isCalendar && !pastGrace) action = 'ignore-mount-grace';
      else if (isCalendar && !submittedRef.current) action = 'pending-await-submit';
      else if (isCalendar) action = 'swap-calendar';
      else if (isStay && isSticky && pastGrace) action = 'lead-stay';
      else if (isStay) action = 'stay';
      else if (isSticky && !pastGrace) action = 'ignore-mount-grace';
      else if (isSticky && (stickyEligible || pendingCalendarHandoffRef.current)) {
        action = 'swap-calendar';
      }
      else if (isSticky) action = 'lead-stay';
      else if (postedHeight != null) {
        action = applyParentHeight ? 'resize-iframe' : 'resize-iframe-skipped';
      }
      else if (!fromGhl && !allowedHandoff) action = 'ignore-origin';

      const income = extractSurveyIncomeLevel(event.data);
      const kind = describeGhlMessageKind(event.data);
      const relevant =
        fromGhl ||
        allowedHandoff ||
        isCalendar ||
        isSticky ||
        isStay ||
        postedHeight != null ||
        age != null;

      if (relevant) {
        logger.info(
          `${SURVEY_AGE_FREEZE_DEBUG_PREFIX} postMessage ${kind} action=${action} age=${age ?? '—'} h=${postedHeight ?? '—'} ${previewGhlMessageData(event.data)}`,
          {
            origin: event.origin,
            kind: isCalendar ? 'calendar' : isStay ? 'stay' : isSticky ? 'sticky' : thankYouKind,
            messageKind: kind,
            pastGrace,
            submitted: submittedRef.current,
            pendingHandoff: pendingCalendarHandoffRef.current,
            calendarEligible: isSticky ? stickyEligible : calendarEligibleRef.current,
            income,
            age,
            postedHeight,
            iframeReadable: readIframeText() != null,
            payloadPreview: previewGhlMessageData(event.data),
            action,
            viteHot: isViteHotClient(),
          },
        );
        recordAgeFreezeDebug({
          source: 'postMessage',
          kind,
          age,
          income,
          postedHeight,
          willChangeSrc: action === 'swap-calendar',
          action,
          persistOnly: deferParentUpdate,
        });
        if (age || postedHeight != null || isSticky) {
          const iframe = document.getElementById(iframeId) as HTMLIFrameElement | null;
          const styleBefore = iframe?.getAttribute('style') ?? null;
          window.requestAnimationFrame(() => {
            const styleAfter = iframe?.getAttribute('style') ?? null;
            if (styleBefore === styleAfter) {
              recordAgeFreezeDebug({
                source: 'outer-script',
                kind: 'style-unchanged-after-message',
                age,
                postedHeight,
                action: kind,
                mutation: styleAfter,
                persistOnly: true,
              });
              return;
            }
            recordAgeFreezeDebug({
              source: 'outer-script',
              kind: age ? 'style-applied-after-age' : 'style-applied-after-message',
              age,
              postedHeight,
              action: kind,
              mutation: `${styleBefore ?? '∅'} -> ${styleAfter ?? '∅'}`,
              persistOnly: true,
            });
          });
        }
      }

      if (isCalendar) {
        if (!pastGrace) return;
        pendingCalendarHandoffRef.current = true;
        const handoffEmail = extractHandoffPixelEmail(event.data);
        if (handoffEmail) surveyEmailRef.current = handoffEmail;
        // Live GHL often loads the calendar without a sticky submit message.
        // Calendar thank-you is the qualify ending — count the survey submit.
        confirmSurveySubmitted();
        scheduleCalendarHandoff('thank-you');
        return;
      }

      if (isStay) {
        pendingCalendarHandoffRef.current = false;
        if (isSticky && pastGrace) {
          rememberSurveyFromSticky(event.data);
          if (pdfRedirectedRef.current) {
            publishSurveyContact();
            return;
          }
          finishSurveyWithPdf();
        }
        return;
      }

      if (!fromGhl) return;
      if (!isSurveySubmissionMessage(event.data)) return;
      if (!pastGrace) return;
      rememberSurveyFromSticky(event.data);
      if (pdfRedirectedRef.current) {
        publishSurveyContact();
        return;
      }
      calendarEligibleRef.current = stickyEligible;
      finishSurveyWithPdf();
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
      if (emailWaitTimerRef.current) {
        window.clearTimeout(emailWaitTimerRef.current);
        emailWaitTimerRef.current = 0;
      }
    };
  }, [
    confirmBookingScheduled,
    confirmSurveySubmitted,
    finishSurveyWithPdf,
    iframeId,
    publishSurveyContact,
    rememberSurveyFromSticky,
    recordAgeFreezeDebug,
    scheduleCalendarHandoff,
  ]);

  // GHL calendar confirmation is a `_top` redirect to conorharris.com/posture-call-booked.
  // Navigation API gives the destination URL before Harry unloads (Chromium).
  useEffect(() => {
    const nav = (window as Window & { navigation?: EventTarget }).navigation;
    if (!nav || typeof nav.addEventListener !== 'function') return;
    const onNavigate = (event: Event) => {
      if (!loadCalendarRef.current) return;
      const url = (event as Event & { destination?: { url?: string } }).destination?.url ?? '';
      if (isGhlBookingThankYouUrl(url)) {
        confirmBookingScheduled();
      }
    };
    nav.addEventListener('navigate', onNavigate);
    return () => nav.removeEventListener('navigate', onNavigate);
  }, [confirmBookingScheduled]);


  return (
    <>
    <Card
      translate="no"
      className={
        fillViewport
          ? 'notranslate border-primary/30 shadow-xl bg-background rounded-2xl overflow-hidden my-0 flex min-h-0 flex-1 flex-col animate-none max-md:border-0 max-md:rounded-none max-md:shadow-none max-md:pointer-events-none'
          : 'notranslate border-primary/30 shadow-xl bg-background rounded-2xl overflow-hidden my-6'
      }
    >
      <CardHeader
        className={
          fillViewport
            ? 'max-md:hidden bg-gradient-to-r from-primary/10 via-primary/5 to-background p-4 sm:p-6 border-b border-primary/10 shrink-0'
            : 'bg-gradient-to-r from-primary/10 via-primary/5 to-background p-4 sm:p-6 border-b border-primary/10 shrink-0'
        }
        data-testid="survey-booking-header"
      >
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-primary text-primary-foreground shadow-md">
            <ClipboardList className="h-6 w-6" />
          </div>
          <div>
            <CardTitle className="text-xl sm:text-2xl font-bold text-foreground font-['Poppins',sans-serif]">
              {headline || 'Fill out this form and book a call'}
            </CardTitle>
            <CardDescription className="text-sm text-muted-foreground font-['Roboto',sans-serif] mt-1">
              A few questions so our team can follow up on your results.
            </CardDescription>
          </div>
        </div>
        {(name || email || postureClassification) && (
          <div className="mt-3 text-xs text-muted-foreground bg-primary/5 border border-primary/10 rounded-lg p-2 flex flex-wrap gap-2 items-center">
            <span>Prefilled for:</span>
            {name && <span className="font-semibold text-foreground">{name}</span>}
            {email && <span className="text-muted-foreground">({email})</span>}
            {postureClassification && (
              <span className="font-medium text-primary bg-primary/10 px-2 py-0.5 rounded">
                Posture: {targetPosture}
              </span>
            )}
          </div>
        )}
      </CardHeader>

      <CardContent
        className={
          fillViewport
            ? 'p-0 sm:p-2 flex-1 min-h-0 overflow-y-auto overscroll-contain max-md:pointer-events-none'
            : 'p-2 sm:p-4'
        }
        style={
          fillViewport
            ? {
                minHeight: 0,
                // Mobile: explicit scroll hint for vertical pan
                WebkitOverflowScrolling: 'touch',
              }
            : { minHeight: `${SURVEY_IFRAME_HEIGHT_PX}px` }
        }
      >
        {/* Edge mobile: keep the iframe at viewport 0,0 (fixed) so device-mode
            hit-testing matches paint. z-40 stays under the dialog Close (z-50).
            Harry chrome is pointer-events-none on mobile so only Close + iframe
            receive taps. Do not apply GHL setHeight here — a parent restyle after
            Name/Age is what left users able to hit only the X.
            translate=no / notranslate: Edge Translate injects `normal?lang=auto`. */}
        {reportReady && onDownloadReport ? (
          <div className="pointer-events-auto relative z-[55] flex min-h-[240px] flex-col items-center justify-center gap-4 px-6 py-10 text-center">
            <p className="text-base text-foreground">Your answers are saved.</p>
            <button
              type="button"
              onClick={onDownloadReport}
              className="inline-flex items-center justify-center rounded-xl bg-primary px-5 py-3 text-base font-bold text-primary-foreground cursor-pointer"
            >
              Download your posture report PDF
            </button>
          </div>
        ) : (
        <iframe
          src={iframeSrc}
          className={
            fillViewport
              ? 'notranslate h-full min-h-0 max-md:!fixed max-md:!inset-0 max-md:!z-40 max-md:!h-[100dvh] max-md:!w-screen max-md:!max-w-none max-md:!pointer-events-auto max-md:!bg-background'
              : 'notranslate'
          }
          translate="no"
          style={{
            width: '100%',
            border: 'none',
            display: 'block',
            height: fillViewport ? '100%' : `${SURVEY_IFRAME_HEIGHT_PX}px`,
          }}
          id={iframeId}
          title="Posture Assessment Booking"
          data-harry-debug-age-freeze={showAgeFreezeHud ? '1' : '0'}
          onLoad={() => {
            const loaded = document.getElementById(iframeId) as HTMLIFrameElement | null;
            const loadedSrc = loaded?.src ?? '';
            if (loadedSrc.includes('/widget/bookings/')) {
              finishSurveyWithPdf();
              return;
            }
            recordAgeFreezeDebug({
              source: 'iframe-src',
              kind: 'iframe-load',
              action: 'loaded-survey',
              persistOnly: true,
            });
            maybeConfirmBookingFromIframe();
          }}
        />
        )}
      </CardContent>
    </Card>
    {showAgeFreezeHud && typeof document !== 'undefined'
      ? createPortal(
          <aside
            data-testid="survey-age-freeze-debug-hud"
            className="pointer-events-none fixed left-0 right-0 top-0 z-[60] max-h-[40vh] overflow-hidden border-b border-border bg-background/90 px-2 py-1 font-mono text-[10px] leading-snug text-foreground"
          >
            <p className="font-semibold">
              {SURVEY_AGE_FREEZE_DEBUG_PREFIX} age-select freeze
            </p>
            <p className="text-muted-foreground">
              {shortUserAgent()}
              {ageFreezeDebugEvents.length
                ? ` vv=${ageFreezeDebugEvents[ageFreezeDebugEvents.length - 1]?.visualViewportHeight ?? '—'} inner=${ageFreezeDebugEvents[ageFreezeDebugEvents.length - 1]?.innerHeight ?? '—'} pos=${ageFreezeDebugEvents[ageFreezeDebugEvents.length - 1]?.iframePosition ?? '—'} scripts=${ageFreezeDebugEvents[ageFreezeDebugEvents.length - 1]?.outerScripts?.join(',') || '—'}`
                : ' waiting for GHL messages… If Edge opens Sources/@react-refresh, turn off Pause on exceptions — that pause is the freeze.'}
            </p>
            <ol className="mt-1 space-y-0.5">
              {ageFreezeDebugEvents.map((event) => (
                <li
                  key={`${event.at}-${event.kind}-${event.action}`}
                  className={event.source === 'outer-script' ? 'text-destructive' : undefined}
                >
                  {formatSurveyAgeFreezeDebugLine(event)}
                </li>
              ))}
            </ol>
          </aside>,
          document.body,
        )
      : null}
    </>
  );
};

export default SurveyBookingWidget;
